<template>
  <div>
    <v-form @submit.prevent="submitResponse">
      <p class="mb-3">
        Enter appropriate identifying information for each chemical used during Fundamental Skills
        and Original Investigation.
      </p>

      <v-simple-table>
        <thead>
          <tr style="height: 76px">
            <td
              v-for="head in headings"
              :key="head.text"
              style="font-weight: bold; text-align: center; border-bottom: 1px solid #e0e0e0"
            >
              {{ head.text }}
            </td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Citric acid</td>
            <td style="width: 150px">
              <chemical-notation-input
                v-model="inputs.chemFormulaCitricAcid"
                class="my-5"
                :show-note="false"
                :input-placeholder="null"
                :disabled="!allowEditing"
              />
            </td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.mmCitricAcid"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td>
              <v-select
                v-model="inputs.physStateCitricAcid"
                :items="stateOptions"
                outlined
                hide-details
              >
                <template #item="{item}">
                  <stemble-latex class="no-text-transform" :content="item" />
                </template>
                <template #selection="{item}">
                  <stemble-latex class="no-text-transform" :content="item" />
                </template>
              </v-select>
            </td>
            <td class="py-2 px-2 mx-0" style="text-align: center">N/A</td>
            <td class="py-2 px-2 mx-0" style="text-align: center">N/A</td>
          </tr>

          <tr>
            <td>Sodium citrate dihydrate</td>
            <td style="width: 150px">
              <chemical-notation-input
                v-model="inputs.chemFormulaNaCitrate"
                class="my-5"
                :show-note="false"
                :input-placeholder="null"
                :disabled="!allowEditing"
              />
            </td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.mmNaCitrate"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td>
              <v-select
                v-model="inputs.physStateNaCitrate"
                :items="stateOptions"
                outlined
                hide-details
              >
                <template #item="{item}">
                  <stemble-latex class="no-text-transform" :content="item" />
                </template>
                <template #selection="{item}">
                  <stemble-latex class="no-text-transform" :content="item" />
                </template>
              </v-select>
            </td>
            <td class="py-2 px-2 mx-0" style="text-align: center">N/A</td>
            <td class="py-2 px-2 mx-0" style="text-align: center">N/A</td>
          </tr>

          <tr>
            <td>Monosodium phosphate</td>
            <td style="width: 150px">
              <chemical-notation-input
                v-model="inputs.chemFormulaMonoSodiumPhosphate"
                class="my-5"
                :show-note="false"
                :input-placeholder="null"
                :disabled="!allowEditing"
              />
            </td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.mmMonoSodiumPhosphate"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td>
              <v-select
                v-model="inputs.physStateMonoSodiumPhosphate"
                :items="stateOptions"
                outlined
                hide-details
              >
                <template #item="{item}">
                  <stemble-latex class="no-text-transform" :content="item" />
                </template>
                <template #selection="{item}">
                  <stemble-latex class="no-text-transform" :content="item" />
                </template>
              </v-select>
            </td>
            <td class="py-2 px-2 mx-0" style="text-align: center">N/A</td>
            <td class="py-2 px-2 mx-0" style="text-align: center">N/A</td>
          </tr>

          <tr>
            <td>Hydrochloric Acid</td>
            <td style="width: 150px">
              <chemical-notation-input
                v-model="inputs.chemFormulaHCl"
                class="my-5"
                :show-note="false"
                :input-placeholder="null"
                :disabled="!allowEditing"
              />
            </td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.mmHCl"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td>
              <v-select v-model="inputs.physStateHCl" :items="stateOptions" outlined hide-details>
                <template #item="{item}">
                  <stemble-latex class="no-text-transform" :content="item" />
                </template>
                <template #selection="{item}">
                  <stemble-latex class="no-text-transform" :content="item" />
                </template>
              </v-select>
            </td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.vHCl"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.cHCl"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
          </tr>

          <tr>
            <td>Sodium Hydroxide</td>
            <td style="width: 150px">
              <chemical-notation-input
                v-model="inputs.chemFormulaNaOH"
                class="my-5"
                :show-note="false"
                :input-placeholder="null"
                :disabled="!allowEditing"
              />
            </td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.mmNaOH"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td>
              <v-select v-model="inputs.physStateNaOH" :items="stateOptions" outlined hide-details>
                <template #item="{item}">
                  <stemble-latex class="no-text-transform" :content="item" />
                </template>
                <template #selection="{item}">
                  <stemble-latex class="no-text-transform" :content="item" />
                </template>
              </v-select>
            </td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.vNaOH"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.cNaOH"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
          </tr>

          <tr>
            <td>pH 7 buffer</td>
            <td style="width: 150px">
              <s-textarea v-model="inputs.chemFormulaBuff" class="mt-7" outlined />
            </td>
            <td>N/A</td>
            <td>
              <v-select v-model="inputs.physStateBuff" :items="stateOptions" outlined hide-details>
                <template #item="{item}">
                  <stemble-latex class="no-text-transform" :content="item" />
                </template>
                <template #selection="{item}">
                  <stemble-latex class="no-text-transform" :content="item" />
                </template>
              </v-select>
            </td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.vBuff"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td>N/A</td>
          </tr>
        </tbody>
      </v-simple-table>

      <p class="mt-5">Select all the glassware/equipment/instrumentation used in this lab.</p>

      <v-select
        v-model="inputs.equipment"
        :items="equipmentOptions"
        multiple
        chips
        outlined
        class="mb-n4 mt-0"
      >
      </v-select>
    </v-form>
  </div>
</template>

<script>
import DynamicQuestionMixin from '../../mixins/dynamic-question';
import StembleLatex from '@/tasks/components/StembleLatex.vue';
import ChemicalNotationInput from '@/chemical-equations/components/ChemicalNotationInput.vue';
import CalculationInput from '@/tasks/components/inputs/CalculationInput.vue';
import STextarea from '@/common/components/STextareaShort.vue';

export default {
  name: 'ChemUCI1LCProject3Quiz1Task11',
  components: {
    STextarea,
    CalculationInput,
    ChemicalNotationInput,
    StembleLatex,
  },
  mixins: [DynamicQuestionMixin()],
  data() {
    return {
      inputs: {
        chemFormulaCitricAcid: null,
        mmCitricAcid: null,
        physStateCitricAcid: null,

        chemFormulaNaCitrate: null,
        mmNaCitrate: null,
        physStateNaCitrate: null,

        chemFormulaMonoSodiumPhosphate: null,
        mmMonoSodiumPhosphate: null,
        physStateMonoSodiumPhosphate: null,

        chemFormulaHCl: null,
        mmHCl: null,
        physStateHCl: null,
        vHCl: null,
        cHCl: null,

        chemFormulaNaOH: null,
        mmNaOH: null,
        physStateNaOH: null,
        vNaOH: null,
        cNaOH: null,

        chemFormulaBuff: null,
        physStateBuff: null,
        vBuff: null,

        equipment: [],
      },
      headings: [
        {text: 'Chemical Name'},
        {text: 'Formula'},
        {text: 'Molar Mass (g/mol)'},
        {text: 'Physical States'},
        {text: 'Total Volume Used (mL)'},
        {text: 'Concentration (M)'},
      ],
      stateOptions: ['l', 's', 'g', 'aq'],
      equipmentOptions: [
        'beaker(s)',
        'Buchner funnel',
        'buret',
        'capillary/melting tube',
        'clamp and stand',
        'cuvette',
        'Digi-Melt (melting point apparatus)',
        'digital balance',
        'disposable pipet',
        'drying oven',
        'Erlenmeyer flask',
        'filter paper',
        'funnel',
        'graduated cylinder',
        'ice',
        'KimWipes (tissues)',
        'LabQuest',
        'pencil',
        'pipet suction bulb',
        'probe (pH)',
        'probe (temperature)',
        'scintillation vial',
        'side arm flask',
        'spatula or scoopula',
        'spectrometer',
        'spotter',
        'stir bar',
        'stirrer/hotplate',
        'test tube',
        'thermometer (alcohol)',
        'TLC (developing) chamber',
        'TLC plate',
        'TLC spotter',
        'UV lamp',
        'volumetric flask',
        'volumetric pipet',
        'watch glass',
        'weighing boat or paper',
      ],
    };
  },
};
</script>
<style scoped></style>
